import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatListModule} from '@angular/material/list';
import {MatLineModule} from '@angular/material/core';
import {CommonModule} from '@angular/common';
import {MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {Subscription} from 'rxjs';
import {MapService} from '../api/services/map.service';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ModelDto, ModelsResponse} from '@maporium-workspace/shared';
import {ModelService} from '../api/services/model.service';
import {CreateModelDialogComponent} from './create-model-dialog.component';
import {CytoscapeService} from '../services/cy-services/cytoscape.service';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {buildLinkShapeSvg, buildNodeShapeSvg,} from '../shared/helpers/legend-shape.helper';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'maporium-model-list',
  styleUrls: ['./model-list.bottomsheet.component.scss'],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="icon-smap-object-model me-2"></span>{{ 'MODEL_LIST.HEADING' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="dismiss()" [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>
    <mat-nav-list class="list-wrapper">
      <a mat-list-item *ngFor="let model of models"
         (click)="$event.preventDefault(); $event.stopPropagation()"
         class="d-flex justify-content-evenly p-0">
        <span class="row w-100 align-items-center m-0">
          <span class="col-2 d-flex justify-content-center align-items-center">
            <span class="model-shape-preview d-inline-flex align-items-center justify-content-center"
                  [innerHTML]="modelIcon(model.id)"
                  [matTooltip]="(model.elementType === 'NODE' ? 'MODEL_LIST.NODE_MODEL_TOOLTIP' : 'MODEL_LIST.LINK_MODEL_TOOLTIP') | translate"></span>
          </span>
          <span class="col-6 pe-5"
                [matTooltip]="model?.description || ''"
                [matTooltipDisabled]="!model?.description"
                matTooltipPosition="below">
            <span matLine>{{model.name}}</span>
          </span>
          <span class="col-4 d-flex justify-content-end">
            <button mat-icon-button
                    [matTooltip]="'MODEL_LIST.EDIT_TOOLTIP' | translate"
                    (click)="edit(model);$event.preventDefault(); $event.stopPropagation()">
              <mat-icon class="material-symbols-rounded">edit</mat-icon>
            </button>

            <button mat-icon-button
                    [matTooltip]="'MODEL_LIST.DELETE_TOOLTIP' | translate"
                    (click)="askForConfirmation(model);$event.preventDefault(); $event.stopPropagation()">
              <mat-icon class="material-symbols-rounded">delete</mat-icon>
            </button>
          </span>
        </span>
      </a>
    </mat-nav-list>
    <mat-divider></mat-divider>
    <div class="py-2" style="padding-left: 10px;">
        <button class="dialog-action-btn dialog-action-btn-primary"
                [matTooltip]="'MODEL_LIST.CREATE_TOOLTIP' | translate"
                (click)="createNew(); $event.preventDefault();">
          {{ 'MODEL_LIST.CREATE_MODEL' | translate }}
        </button>
    </div>
  `,

  standalone: true,
  imports: [
    MatListModule,
    CommonModule,
    MatLineModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslatePipe,
  ]
})
export class ModelListBottomsheetComponent implements OnInit, OnDestroy {
  public models: ModelDto[] = [];
  private modelIconCache = new Map<string, SafeHtml>();
  private subSink: Subscription = new Subscription();

  constructor(
    private matDialog: MatDialog,
    private modelService: ModelService,
    private mapService: MapService,
    private cyService: CytoscapeService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private bottomSheetRef: MatBottomSheetRef<ModelListBottomsheetComponent>
  ) {}

  modelIcon(modelId: string): SafeHtml | undefined {
    return this.modelIconCache.get(modelId);
  }

  private rebuildModelIconCache(): void {
    this.modelIconCache.clear();
    for (const m of this.models) {
      const original = m.elementDefinition?.originalElement;
      const svg = m.elementType === 'NODE'
        ? buildNodeShapeSvg(original, {size: 20, strokeWidth: 2})
        : buildLinkShapeSvg(original, {size: 24, strokeWidth: 2});
      this.modelIconCache.set(m.id, this.sanitizer.bypassSecurityTrustHtml(svg));
    }
  }

  ngOnInit() {
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (mapId) {
      this.subSink.add(
        this.modelService.getAll(mapId).subscribe((response: ModelsResponse) => {
          // Combine node and link models into one sorted array, ordered alphabetically by name (A-Z)
          this.models = [...response.nodeModels, ...response.linkModels].sort((a: ModelDto, b: ModelDto) => {
            return a.name.localeCompare(b.name);
          });
          this.rebuildModelIconCache();
        })
      );
    }
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  hasSelection(): boolean {
    const cy = this.cyService.cy;
    if (!cy) return false;
    return cy.$(':selected').length === 1;
  }

  createNew() {
    // Validate selection first
    const cy = this.cyService.cy;
    if (!cy) return;

    const selected = cy.$(':selected');
    if (selected.length === 0) {
      // Show warning dialog for no selection
      this.matDialog.open(ConfirmationDialogComponent, {
        width: '540px',
        maxWidth: '90vw',
        hasBackdrop: false,
        data: {
          title: this.translate.instant('MODEL_LIST.APPLY_MODEL_TITLE'),
          icon: 'warning',
          message: this.translate.instant('MODEL_LIST.NO_SELECTION_MESSAGE'),
          confirmText: this.translate.instant('MODEL_LIST.OK'),
          showCancel: false,
          showDoNotAskAgain: true
        }
      });
      return;
    }

    if (selected.length > 1) {
      // Show warning dialog for multiple selection
      this.matDialog.open(ConfirmationDialogComponent, {
        width: '540px',
        maxWidth: '90vw',
        hasBackdrop: false,
        data: {
          title: this.translate.instant('MODEL_LIST.MULTIPLE_SELECTION_TITLE'),
          icon: 'warning',
          message: this.translate.instant('MODEL_LIST.MULTIPLE_SELECTION_MESSAGE'),
          confirmText: this.translate.instant('MODEL_LIST.OK'),
          showCancel: false,
          showDoNotAskAgain: true
        }
      });
      return;
    }

    // Open create dialog without closing the bottomsheet
    const ref = this.matDialog.open(CreateModelDialogComponent, {
      width: '35%',
      autoFocus: 'input',
      hasBackdrop: false
    });

    ref.afterClosed().subscribe((newModel: ModelDto) => {
      if (newModel) {
        // Add the new model to the list and sort
        this.models.push(newModel);
        this.models.sort((a: ModelDto, b: ModelDto) => a.name.localeCompare(b.name));
        this.rebuildModelIconCache();
        this.modelService.modelsChangedSubject.next();
      }
    });
  }

  edit(model: ModelDto) {
    const ref = this.matDialog.open(CreateModelDialogComponent, {
      data: { model },
      width: '35%',
      autoFocus: 'input',
      hasBackdrop: false
    });
    ref.afterClosed().subscribe((updatedModel: ModelDto) => {
      if (updatedModel) {
        this.models = this.models
          .map((m) => m.id === updatedModel.id ? updatedModel : m)
          .sort((a: ModelDto, b: ModelDto) => a.name.localeCompare(b.name));
        this.rebuildModelIconCache();
        this.modelService.modelsChangedSubject.next();
      }
    });
  }

  delete(model: ModelDto) {
    this.modelService.delete(model.id).subscribe(() => {
      this.models = this.models.filter((m) => m.id !== model.id);
      this.modelIconCache.delete(model.id);
      this.modelService.modelsChangedSubject.next();
    });
  }

  dismiss() {
    this.bottomSheetRef.dismiss();
  }

  askForConfirmation(model: ModelDto) {
    const ref = this.matDialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      hasBackdrop: false,
      data: {
        title: this.translate.instant('MODEL_LIST.DELETE_MODEL_TITLE'),
        icon: 'delete',
        message: this.translate.instant('MODEL_LIST.DELETE_MODEL_MESSAGE', { name: model.name }),
        confirmText: this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_CONFIRM')
      }
    });

    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.delete(model);
      }
    });
  }
}
